import { useEffect, useMemo, useState } from "react";
import { Headphones, MessageSquare, Send, ShieldCheck } from "lucide-react";
import {
  SUPPORT_PRIORITIES,
  SUPPORT_STATUSES,
  addSupportMessage,
  subscribeSupportMessages,
  subscribeSupportTickets,
  updateSupportTicket
} from "../services/supportService";

const label = {
  new: "Yeni", open: "Açık", waiting_customer: "Müşteri bekleniyor", resolved: "Çözüldü", closed: "Kapalı",
  low: "Düşük", normal: "Normal", high: "Yüksek", urgent: "Acil"
};

const timeText = (value) => value?.toDate?.().toLocaleString("tr-TR") || "Şimdi";

function SupportOperationsView({ user, users = [], themeMode = "light" }) {
  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("active");
  const [draft, setDraft] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const dark = themeMode === "dark";
  const surface = dark ? "#162133" : "#fff";
  const border = dark ? "#334155" : "#dbe2ea";
  const text = dark ? "#e5edf6" : "#124254";
  const muted = dark ? "#9fb0c4" : "#64748b";

  useEffect(() => subscribeSupportTickets((items) => {
    setTickets(items);
    setSelectedId((current) => current || items[0]?.id || "");
  }, (reason) => setError(reason.message)), []);

  useEffect(() => {
    if (!selectedId) { setMessages([]); return undefined; }
    return subscribeSupportMessages(selectedId, setMessages, (reason) => setError(reason.message));
  }, [selectedId]);

  const selected = tickets.find((item) => item.id === selectedId);
  const visibleTickets = useMemo(() => tickets.filter((item) => (
    filter === "all" || (filter === "active" && !["resolved", "closed"].includes(item.status)) || item.status === filter
  )), [filter, tickets]);

  const patchTicket = async (changes) => {
    if (!selected) return;
    setSaving(true);
    try { await updateSupportTicket(selected.id, changes); } catch (reason) { setError(reason.message); } finally { setSaving(false); }
  };

  const sendMessage = async () => {
    if (!selected || !draft.trim()) return;
    setSaving(true);
    try {
      await addSupportMessage({ ticketId: selected.id, body: draft, visibility, user });
      await updateSupportTicket(selected.id, {
        lastMessageAt: new Date(),
        status: visibility === "public" ? "waiting_customer" : selected.status
      });
      setDraft("");
    } catch (reason) { setError(reason.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ color: text }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
        <div><h2 style={{ margin: 0 }}>Destek ve Müşteri Başarı Merkezi</h2><p style={{ color: muted, fontWeight: 700 }}>Tenant ayrımlı talepler, kuyruklar ve gerçek zamanlı yazışmalar</p></div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#0f766e", fontWeight: 900 }}><ShieldCheck size={19} /> Merkezi destek operasyonu</div>
      </div>
      {error && <div style={{ padding: 12, marginBottom: 12, borderRadius: 12, background: "#fee2e2", color: "#991b1b" }}>{error}</div>}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 0.85fr) minmax(440px, 1.5fr)", gap: 16 }}>
        <section style={{ background: surface, border: `1px solid ${border}`, borderRadius: 18, overflow: "hidden" }}>
          <div style={{ padding: 14, borderBottom: `1px solid ${border}`, display: "flex", gap: 8 }}>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{ flex: 1, padding: 10, borderRadius: 10, border: `1px solid ${border}` }}>
              <option value="active">Aktif kuyruk</option><option value="all">Tümü</option>
              {SUPPORT_STATUSES.map((item) => <option key={item} value={item}>{label[item]}</option>)}
            </select>
          </div>
          <div style={{ maxHeight: 650, overflowY: "auto" }}>
            {visibleTickets.map((ticket) => <button key={ticket.id} onClick={() => setSelectedId(ticket.id)} style={{ width: "100%", textAlign: "left", padding: 14, border: 0, borderBottom: `1px solid ${border}`, background: selectedId === ticket.id ? (dark ? "#20334a" : "#ecfeff") : "transparent", color: text, cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><strong>{ticket.subject}</strong><span style={{ color: ticket.priority === "urgent" ? "#dc2626" : muted, fontSize: 12, fontWeight: 900 }}>{label[ticket.priority] || ticket.priority}</span></div>
              <div style={{ marginTop: 7, color: muted, fontSize: 12 }}>{ticket.companyName || ticket.companyId} · {ticket.category || "Genel"}</div>
              <div style={{ marginTop: 7, fontSize: 12, fontWeight: 800 }}>{label[ticket.status] || ticket.status}</div>
            </button>)}
            {!visibleTickets.length && <div style={{ padding: 24, textAlign: "center", color: muted }}><Headphones size={28} /><p>Kuyrukta talep yok.</p></div>}
          </div>
        </section>
        <section style={{ background: surface, border: `1px solid ${border}`, borderRadius: 18, minHeight: 560, display: "flex", flexDirection: "column" }}>
          {!selected ? <div style={{ margin: "auto", color: muted, textAlign: "center" }}><MessageSquare size={34} /><p>Bir destek talebi seçin.</p></div> : <>
            <div style={{ padding: 16, borderBottom: `1px solid ${border}` }}>
              <h3 style={{ margin: "0 0 6px" }}>{selected.subject}</h3>
              <div style={{ color: muted, fontSize: 12 }}>{selected.tenantId} / {selected.companyId} · #{selected.id.slice(0, 8)}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 13 }}>
                <select disabled={saving} value={selected.status} onChange={(e) => patchTicket({ status: e.target.value })}>{SUPPORT_STATUSES.map((item) => <option key={item} value={item}>{label[item]}</option>)}</select>
                <select disabled={saving} value={selected.priority} onChange={(e) => patchTicket({ priority: e.target.value })}>{SUPPORT_PRIORITIES.map((item) => <option key={item} value={item}>{label[item]}</option>)}</select>
                <select disabled={saving} value={selected.assigneeId || ""} onChange={(e) => patchTicket({ assigneeId: e.target.value, assigneeName: e.target.selectedOptions[0]?.text || "" })}><option value="">Atanmamış</option>{users.filter((item) => item.isActive !== false).map((item) => <option key={item.id || item.uid} value={item.id || item.uid}>{item.name || item.email}</option>)}</select>
              </div>
            </div>
            <div style={{ flex: 1, padding: 16, overflowY: "auto", maxHeight: 430 }}>
              {messages.map((message) => <div key={message.id} style={{ marginBottom: 12, padding: 12, borderRadius: 14, background: message.visibility === "internal" ? (dark ? "#3b3420" : "#fffbeb") : (dark ? "#20334a" : "#f1f5f9"), borderLeft: `4px solid ${message.visibility === "internal" ? "#f59e0b" : "#0f766e"}` }}><div style={{ display: "flex", justifyContent: "space-between", color: muted, fontSize: 11, fontWeight: 800 }}><span>{message.senderName} · {message.visibility === "internal" ? "İç not" : "Müşteriye açık"}</span><span>{timeText(message.createdAt)}</span></div><div style={{ marginTop: 7, whiteSpace: "pre-wrap" }}>{message.body}</div></div>)}
            </div>
            <div style={{ padding: 14, borderTop: `1px solid ${border}` }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}><button onClick={() => setVisibility("public")} style={{ fontWeight: 800, color: visibility === "public" ? "#0f766e" : muted }}>Müşteriye yanıt</button><button onClick={() => setVisibility("internal")} style={{ fontWeight: 800, color: visibility === "internal" ? "#b45309" : muted }}>İç not</button></div>
              <div style={{ display: "flex", gap: 8 }}><textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} placeholder={visibility === "internal" ? "Yalnızca iç ekibin göreceği not..." : "Müşteriye gönderilecek yanıt..."} style={{ flex: 1, resize: "vertical", borderRadius: 12, padding: 11, border: `1px solid ${border}` }} /><button disabled={saving || !draft.trim()} onClick={sendMessage} style={{ border: 0, borderRadius: 12, padding: "0 18px", background: "#0f766e", color: "white", cursor: "pointer" }}><Send size={18} /></button></div>
            </div>
          </>}
        </section>
      </div>
    </div>
  );
}

export default SupportOperationsView;
